import React from 'react';
import { Menu, Tooltip } from '@mantine/core';
import { IconTarget, IconArrowBackUp } from '@tabler/icons-react';
import { PartnerViewExtensions } from '../components/Partners/views/PartnerViewExtensions';
import type { PartnerActiveLead } from '../components/Partners/views/PartnerViewExtensions';
import { CRM_LEAD_TYPES } from '../../constants';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useAuthController } from '@ayocore/platform/identity/client';
import { CrmExtensions } from '../extensions';

// ---------------------------------------------------------------------------
// Пропсы для модального окна. useModal требует P extends Record<string, unknown>,
// поэтому наследуемся от него явно.
// ---------------------------------------------------------------------------
interface PartnerLeadCreateModalProps extends Record<string, unknown> {
  defaultLeadTypeSlug: string;
  partner: { id: number; name: string };
  /** Ответственный по партнёру — подставляется как manager_id */
  manager?: { id: number; name: string };
  onLeadCreated?: (leadId?: number) => void;
}

/**
 * Обёртка над CreateLeadForm для работы с useModal.
 * title пробрасывается как заголовок модалки (useModal вытаскивает title из пропсов).
 * Автоматически заполняет:
 *  — partner_id из переданного partner
 *  — title: "{partner.name} (удержание/возврат)"
 *  — description: "Лид сформирован из карточки партнера пользователем {user.name}"
 */
function PartnerLeadCreateModal({
  defaultLeadTypeSlug,
  partner,
  manager,
  onLeadCreated,
}: PartnerLeadCreateModalProps) {
  const { user } = useAuthController();
  const CreateLeadForm = CrmExtensions.Leads.createForm.get();

  const leadTypeLabel = defaultLeadTypeSlug === CRM_LEAD_TYPES.RETENTION ? 'удержание' : 'возврат';
  const title = `${partner.name} (${leadTypeLabel})`;
  const description = user?.name
    ? `Лид сформирован из карточки партнера пользователем ${user.name}`
    : undefined;

  return (
    <CreateLeadForm
      defaultLeadTypeSlug={defaultLeadTypeSlug}
      initialData={{
        partner_id: partner.id,
        title,
        description,
      }}
      partner={partner}
      manager={manager}
      onLeadCreated={onLeadCreated}
    />
  );
}

// ---------------------------------------------------------------------------
// Компонент одного пункта меню
// ---------------------------------------------------------------------------
interface CreateLeadMenuItemProps {
  leadTypeSlug: string;
  label: string;
  icon: React.ReactNode;
  partner: { id: number; name: string };
  /** Ответственный по партнёру */
  manager?: { id: number; name: string };
  onCreated: () => void;
  /**
   * Активный лид этого же типа, блокирующий создание нового.
   * Если передан — пункт показывается disabled с тултипом-объяснением
   * (серверное правило lead_active_uniqueness_rules всё равно не пропустит).
   */
  blockingLead?: PartnerActiveLead;
}

function CreateLeadMenuItem({
  leadTypeSlug,
  label,
  icon,
  partner,
  manager,
  onCreated,
  blockingLead,
}: CreateLeadMenuItemProps) {
  const modal = useModal<PartnerLeadCreateModalProps>(PartnerLeadCreateModal);

  const handleClick = () => {
    modal.open(
      {
        defaultLeadTypeSlug: leadTypeSlug,
        partner,
        manager,
        onLeadCreated: () => {
          modal.close();
          onCreated();
        },
        title: label,
      },
      { size: 'lg' }
    );
  };

  if (blockingLead) {
    // Mantine disabled-пункт гасит события мыши и тултип не срабатывает,
    // поэтому «выключенность» имитируем стилем, а клик просто игнорируем
    return (
      <Tooltip
        label={`Уже есть активный лид «${blockingLead.leadTypeName ?? label}»: #${blockingLead.id} «${blockingLead.title}». Завершите или архивируйте его.`}
        multiline
        maw={300}
        position='left'
        withArrow
      >
        <Menu.Item
          leftSection={icon}
          closeMenuOnClick={false}
          style={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          {label}
        </Menu.Item>
      </Tooltip>
    );
  }

  return (
    <Menu.Item leftSection={icon} onClick={handleClick}>
      {label}
    </Menu.Item>
  );
}

// ---------------------------------------------------------------------------
// Публичная функция регистрации — вызывается один раз из bootstrap.ts
// ---------------------------------------------------------------------------
export function registerPartnerLeadCreationExtensions(): void {
  // Вспомогательная функция: достаём менеджера из полного объекта партнёра
  const extractManager = (p: {
    manager?: { id?: number; name?: string; email?: string } | null;
  }): { id: number; name: string } | undefined => {
    if (p.manager?.id) {
      return { id: p.manager.id, name: p.manager.name ?? '' };
    }
    return undefined;
  };

  // ——— RETENTION (удержание клиента) ———
  PartnerViewExtensions.menuItems.register(({ partner: p, reload, canEdit, activeLeads }) => {
    if (!canEdit) return null;

    const partner = { id: p.id, name: p.name };
    const manager = extractManager(p);
    // Активный лид этого же типа блокирует создание нового — пункт disabled с тултипом
    const blockingLead = activeLeads.find(l => l.leadTypeSlug === CRM_LEAD_TYPES.RETENTION);

    return (
      <CreateLeadMenuItem
        key='retention-lead'
        leadTypeSlug={CRM_LEAD_TYPES.RETENTION}
        label='Удержание клиента'
        icon={<IconTarget size={14} />}
        partner={partner}
        manager={manager}
        onCreated={reload}
        blockingLead={blockingLead}
      />
    );
  });

  // ——— WINBACK (возврат клиента) ———
  PartnerViewExtensions.menuItems.register(({ partner: p, reload, canEdit, activeLeads }) => {
    if (!canEdit) return null;

    const partner = { id: p.id, name: p.name };
    const manager = extractManager(p);
    const blockingLead = activeLeads.find(l => l.leadTypeSlug === CRM_LEAD_TYPES.WINBACK);

    return (
      <CreateLeadMenuItem
        key='winback-lead'
        leadTypeSlug={CRM_LEAD_TYPES.WINBACK}
        label='Возврат клиента'
        icon={<IconArrowBackUp size={14} />}
        partner={partner}
        manager={manager}
        onCreated={reload}
        blockingLead={blockingLead}
      />
    );
  });
}
